/**
 * MessageDisclaimer
 *
 * Contextual info below the message editor: char count, SMS warnings,
 * and available template variables per trigger type.
 *
 * Migrated from v2 text-disclaimer.hbs + client-reminder-message.hbs:120-164
 */

import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';

import { getVarsForTrigger } from '../automationMessageVariables';
import { AutomationItem } from '../types';

interface MessageDisclaimerProps {
  automation: AutomationItem;
  hasDefaultMessage?: boolean;
}

const getTextCount = (len: number): number => {
  if (len === 0) return 0;
  if (len <= 160) return 1;
  return Math.ceil(len / 153);
};

export const MessageDisclaimer: FC<MessageDisclaimerProps> = ({
  automation,
  hasDefaultMessage = false,
}) => {
  const { t } = useTranslation();
  const { session } = useSession();

  const msg = Array.isArray(automation.content) ? automation.content[0] ?? '' : '';
  const charCount = msg.length;
  const hasSms = Array.isArray(automation.media) && automation.media.includes('text');
  const smsLimit = session?.doNotTruncateSms ? 1600 : 160;
  const vars = getVarsForTrigger(automation.reminderType || '');

  return (
    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
      {charCount} {t('automations.common.disclaimer.characters')}

      {hasSms && (
        <Box sx={{ mt: 0.5 }}>
          {hasDefaultMessage
            ? t('automations.common.disclaimer.smsTruncatedWithDefault', { limit: smsLimit })
            : t('automations.common.disclaimer.smsTruncated', { limit: smsLimit })}
          {charCount > 160 && (
            <Box sx={{ mt: 0.5 }}>
              {t('automations.common.disclaimer.smsMultipleTexts', { count: getTextCount(charCount) })}
            </Box>
          )}
        </Box>
      )}

      {vars.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Trans
            i18nKey="automations.common.disclaimer.availableVars"
            values={{ vars: vars.join(', ') }}
            components={{ b: <strong /> }}
          />
        </Box>
      )}
    </Typography>
  );
};
